import { useCallback, useEffect, useRef, useState } from 'react';
import {
    confirmDocumentUpload,
    createDocument,
    getDocumentStatus,
    removeDocument,
    uploadToLink,
} from '../api/document-upload';
import {
    MAX_CONCURRENT_UPLOADS,
    MESSAGES,
    ROW_STATES,
    addDraftDocument,
    applyStatus,
    checkFile,
    draftDocuments,
    isPasswordProtected,
    needsPasswordCheck,
    removeDraftDocument,
    rowFromStatus,
    slotsUsed,
    sortFiles,
} from '../../components/Pages/Enquiries/RaiseEnquiry/documentUpload';

const CONFIRM_RETRY_DELAY_MS = 1000;

let rowCounter = 0;
const nextRowKey = () => {
    rowCounter += 1;
    return `upload-${Date.now()}-${rowCounter}`;
};

const readFileBytes = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });

const refusalMessage = (err) => {
    const status = err?.response?.status;
    const errors = err?.response?.data?.errors;
    if (status === 400 && Array.isArray(errors) && errors.length > 0) return errors.join(' ');
    return MESSAGES.failed;
};

const statusData = (resp) => resp?.data?.data || null;

export function useDocumentUploads({ enabled, draft, setDraft, submissionReference }) {
    const [rows, setRows] = useState([]);
    const [refused, setRefused] = useState([]);
    const queue = useRef([]);
    const active = useRef(0);
    const cancelled = useRef(new Set());
    const loadedForReference = useRef(null);
    const draftRef = useRef(draft);
    draftRef.current = draft;

    const updateRow = useCallback((key, change) => {
        setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change(row) } : row)));
    }, []);

    const reloadStatuses = useCallback(() => {
        const documents = draftDocuments(draftRef.current);
        return Promise.all(
            documents.map((document) =>
                getDocumentStatus(document.documentDomainId)
                    .then((resp) => ({ document, data: statusData(resp), known: true }))
                    .catch((err) => ({ document, data: null, known: err?.response?.status === 404 }))
            )
        ).then((results) => {
            setRows((current) => {
                const loaded = results.map(({ document, data, known }) => {
                    const existing = current.find((row) => row.documentDomainId === document.documentDomainId);
                    if (!known) return existing || { ...rowFromStatus(document, { status: 'failed', result: 'failed' }) };
                    if (!data) return { ...(existing || {}), ...rowFromStatus(document, null) };
                    return existing ? applyStatus(existing, data) : rowFromStatus(document, data);
                });
                const notYetCreated = current.filter((row) => !row.documentDomainId);
                return [...loaded, ...notYetCreated];
            });
        });
    }, []);

    useEffect(() => {
        if (!enabled || loadedForReference.current === submissionReference) return;
        loadedForReference.current = submissionReference;
        reloadStatuses();
    }, [enabled, submissionReference, reloadStatuses]);

    const isCancelled = (key) => cancelled.current.has(key);

    const uploadOne = useCallback(
        async ({ key, file }) => {
            try {
                if (needsPasswordCheck(file.name)) {
                    const bytes = await readFileBytes(file);
                    if (isPasswordProtected(file.name, bytes)) {
                        updateRow(key, () => ({ state: ROW_STATES.FAILED, message: MESSAGES.passwordProtected, progress: undefined }));
                        return;
                    }
                }
                if (isCancelled(key)) return;

                let created;
                try {
                    created = statusData(
                        await createDocument({
                            fileName: file.name,
                            sizeBytes: file.size,
                            contentType: file.type,
                            submissionReference,
                        })
                    );
                } catch (err) {
                    updateRow(key, () => ({ state: ROW_STATES.FAILED, message: refusalMessage(err), progress: undefined }));
                    return;
                }
                if (!created?.documentDomainId || !created.uploadUrl) {
                    updateRow(key, () => ({ state: ROW_STATES.FAILED, progress: undefined }));
                    return;
                }

                const documentDomainId = created.documentDomainId;
                const abandon = () => {
                    setDraft((current) => removeDraftDocument(current, documentDomainId));
                    removeDocument(documentDomainId).catch(() => {});
                };
                if (isCancelled(key)) {
                    removeDocument(documentDomainId).catch(() => {});
                    return;
                }
                setDraft((current) => addDraftDocument(current, { documentDomainId, fileName: file.name }));
                updateRow(key, () => ({ documentDomainId }));

                try {
                    await uploadToLink(created.uploadUrl, file, (progress) => updateRow(key, () => ({ progress })));
                } catch (err) {
                    if (isCancelled(key)) {
                        abandon();
                        return;
                    }
                    updateRow(key, () => ({ state: ROW_STATES.FAILED, progress: undefined }));
                    return;
                }
                if (isCancelled(key)) {
                    abandon();
                    return;
                }

                let confirmed = null;
                for (let attempt = 0; attempt < 2 && !confirmed; attempt += 1) {
                    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_DELAY_MS));
                    try {
                        confirmed = statusData(await confirmDocumentUpload(documentDomainId));
                    } catch (err) {
                        confirmed = null;
                    }
                }
                if (isCancelled(key)) {
                    abandon();
                    return;
                }

                if (!confirmed) {
                    updateRow(key, () => ({ state: ROW_STATES.FAILED, progress: undefined }));
                    return;
                }
                setRows((current) => current.map((row) => (row.key === key ? applyStatus({ ...row, statusSequence: 0 }, confirmed) : row)));
            } catch (err) {
                updateRow(key, () => ({ state: ROW_STATES.FAILED, progress: undefined }));
            }
        },
        [setDraft, submissionReference, updateRow]
    );

    const pump = useCallback(() => {
        while (active.current < MAX_CONCURRENT_UPLOADS && queue.current.length > 0) {
            const item = queue.current.shift();
            if (isCancelled(item.key)) continue;
            active.current += 1;
            uploadOne(item).finally(() => {
                active.current -= 1;
                pump();
            });
        }
    }, [uploadOne]);

    const addFiles = useCallback(
        (fileList) => {
            const files = Array.from(fileList || []);
            if (files.length === 0) return;
            const { accepted, refused: refusedFiles } = sortFiles(files, slotsUsed(rows));
            setRefused(refusedFiles);
            if (accepted.length === 0) return;
            const items = accepted.map((file) => ({ key: nextRowKey(), file }));
            setRows((current) => [
                ...current,
                ...items.map(({ key, file }) => ({ key, fileName: file.name, state: ROW_STATES.UPLOADING, progress: 0 })),
            ]);
            queue.current.push(...items);
            pump();
        },
        [rows, pump]
    );

    const forgetDocument = useCallback(
        (row) => {
            if (!row.documentDomainId) return;
            setDraft((current) => removeDraftDocument(current, row.documentDomainId));
            if (row.state !== ROW_STATES.REMOVED) removeDocument(row.documentDomainId).catch(() => {});
        },
        [setDraft]
    );

    const remove = useCallback(
        (row) => {
            cancelled.current.add(row.key);
            setRefused([]);
            setRows((current) => current.filter((r) => r.key !== row.key));
            forgetDocument(row);
        },
        [forgetDocument]
    );

    const replace = useCallback(
        (row, file) => {
            if (!file) return;
            const problem = checkFile(file);
            if (problem) {
                setRefused([{ fileName: file.name, message: problem }]);
                return;
            }
            setRefused([]);
            cancelled.current.add(row.key);
            forgetDocument(row);
            const key = nextRowKey();
            setRows((current) =>
                current.map((r) =>
                    r.key === row.key ? { key, fileName: file.name, state: ROW_STATES.UPLOADING, progress: 0 } : r
                )
            );
            queue.current.push({ key, file });
            pump();
        },
        [forgetDocument, pump]
    );

    return { rows, refused, addFiles, remove, replace, reloadStatuses };
}
